import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Send, ExternalLink } from "lucide-react";
import FunderShell from "./FunderShell";
import { SectionCard, MetricCard, StageLabel } from "./FunderUI";
import { getDecisions } from "../../service/FunderPortalService";

const DEC = {
    Fund: { color: "var(--success)", bg: "var(--success-bg)" },
    Hold: { color: "var(--warning)", bg: "var(--warning-bg)" },
    Decline: { color: "var(--danger-muted)", bg: "var(--danger-muted-bg)" },
};
const FB = {
    Shared: { color: "var(--success)", bg: "var(--success-bg)" },
    Drafting: { color: "var(--warning)", bg: "var(--warning-bg)" },
    Pending: { color: "var(--ink-450)", bg: "var(--surface-4)" },
};

// "$18.6M" -> 18.6
const num = (s) => (typeof s === "string" ? parseFloat(s.replace(/[^0-9.]/g, "")) : Number(s) || 0);

function Inner({ callId, call }) {
    const navigate = useNavigate();
    const [decisions, setDecisions] = useState(() => getDecisions(callId).decisions);
    const [selected, setSelected] = useState(null);
    const [sent, setSent] = useState(false);

    useEffect(() => {
        setDecisions(getDecisions(callId).decisions);
        setSelected(null);
        setSent(false);
    }, [callId]);

    const { summary, policyNote } = getDecisions(callId);

    const counts = useMemo(() => ({
        Fund: decisions.filter((d) => d.decision === "Fund").length,
        Hold: decisions.filter((d) => d.decision === "Hold").length,
        Decline: decisions.filter((d) => d.decision === "Decline").length,
    }), [decisions]);

    const committed = num(summary.budgetCommitted);
    const available = num(summary.budgetAvailable) || 1;
    const budgetPct = Math.min(100, Math.round((committed / available) * 100));

    const setDecision = (grantNumber, value) =>
        setDecisions((prev) => prev.map((d) => (d.grantNumber === grantNumber ? { ...d, decision: value } : d)));

    const sel = decisions.find((d) => d.grantNumber === selected) || null;

    return (
        <div className="funder-section-stack">
            <div>
                <StageLabel>Stage 6 · Decision &amp; feedback</StageLabel>
                <h1 className="funder-h1" style={{ color: "var(--ink-900)", margin: "6px 0 4px" }}>Decisions &amp; Feedback</h1>
                <p style={{ color: "var(--ink-450)", fontSize: 13.5, margin: 0 }}>
                    Record funding decisions and manage applicant feedback for {call.round}.
                </p>
            </div>

            {/* Summary */}
            <div className="funder-metric-grid">
                <MetricCard label="Funded" value={counts.Fund}><div style={{ fontSize: 12, color: "var(--success)", marginTop: 4 }}>recommended to Decision Board</div></MetricCard>
                <MetricCard label="Held for discussion" value={counts.Hold}><div style={{ fontSize: 12, color: "var(--warning)", marginTop: 4 }}>pending further review</div></MetricCard>
                <MetricCard label="Declined" value={counts.Decline}><div style={{ fontSize: 12, color: "var(--danger-muted)", marginTop: 4 }}>not progressing this round</div></MetricCard>
                <MetricCard label="Budget committed">
                    <div className="funder-metric-value" style={{ fontSize: 22 }}>{summary.budgetCommitted} <span style={{ fontSize: 13, color: "var(--ink-350)", fontWeight: 400 }}>of {summary.budgetAvailable}</span></div>
                    <div className="funder-bar-track" style={{ marginTop: 8 }}><div className="funder-bar-fill" style={{ width: `${budgetPct}%`, background: "var(--brand-text)" }} /></div>
                    <div className="funder-metric-sub">{budgetPct}% of the call budget</div>
                </MetricCard>
            </div>

            {/* Decisions table */}
            <SectionCard kicker="Decision register" title="Proposal decisions" subtitle="Set each proposal's outcome and track applicant-feedback status.">
                <div style={{ overflowX: "auto" }}>
                    <table className="funder-table">
                        <thead>
                            <tr>
                                <th>Grant #</th>
                                <th>Proposal</th>
                                <th style={{ textAlign: "center" }}>DT</th>
                                <th style={{ textAlign: "center" }}>Committee</th>
                                <th>Decision</th>
                                <th>Feedback</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {decisions.map((d) => {
                                const dec = DEC[d.decision] || DEC.Hold;
                                const fb = FB[d.feedback?.status] || FB.Pending;
                                return (
                                    <tr key={d.grantNumber} className={selected === d.grantNumber ? "funder-row-expanded" : undefined}>
                                        <td className="funder-grant-no">{d.grantNumber}</td>
                                        <td className="funder-title-cell">
                                            <span style={{ color: "var(--ink-900)" }}>{d.title}</span>
                                            {d.outOfRemit && <span className="funder-out-pill" style={{ marginLeft: 8 }}>Out of scope</span>}
                                        </td>
                                        <td style={{ textAlign: "center" }}><span className="funder-dt-chip">{d.dt}</span></td>
                                        <td style={{ textAlign: "center", fontWeight: 600 }}>{Number(d.committee).toFixed(1)}</td>
                                        <td>
                                            <select
                                                value={d.decision}
                                                onChange={(e) => setDecision(d.grantNumber, e.target.value)}
                                                aria-label={`Decision for ${d.grantNumber}`}
                                                style={{ padding: "5px 9px", borderRadius: 7, border: `1px solid ${dec.color}`, background: dec.bg, color: dec.color, fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}
                                            >
                                                <option value="Fund">Fund</option>
                                                <option value="Hold">Hold</option>
                                                <option value="Decline">Decline</option>
                                            </select>
                                        </td>
                                        <td>
                                            <span className="status-badge" style={{ background: fb.bg, color: fb.color }}>
                                                {d.feedback?.status || "Pending"}{d.feedback?.date ? ` · ${d.feedback.date}` : ""}
                                            </span>
                                        </td>
                                        <td>
                                            <button className="funder-btn-ghost" onClick={() => setSelected(d.grantNumber)}>View feedback</button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </SectionCard>

            {/* Feedback / decision-letter panel */}
            <SectionCard kicker="Applicant feedback" title={sel ? `Decision letter · ${sel.grantNumber}` : "Decision letters"} subtitle={sel ? sel.title : "Select a proposal above to preview the letter that will be shared with the applicant."}>
                {sel ? (
                    <div>
                        <div style={{ border: "1px solid var(--border)", borderRadius: 12, padding: 18, background: "var(--surface-2)", fontSize: 13.5, lineHeight: 1.6, color: "var(--ink-750)" }}>
                            <p style={{ marginTop: 0 }}>Dear Applicant,</p>
                            <p>
                                Following the review committee meeting for <strong>{call.round}</strong>, your proposal
                                {" "}<strong>{sel.grantNumber}</strong> — “{sel.title}” — received a committee score of
                                {" "}<strong>{Number(sel.committee).toFixed(1)}/10</strong>.
                            </p>
                            <p>
                                {sel.decision === "Fund" && "We are pleased to recommend your proposal for funding, subject to ratification by the Decision Board."}
                                {sel.decision === "Hold" && "Your proposal has been held for further discussion at the Decision Board and a final outcome will follow."}
                                {sel.decision === "Decline" && "We regret that, on this occasion, your proposal will not be progressing to funding."}
                                {" "}Unattributed comments from the expert reviewers are enclosed to support your future work.
                            </p>
                            <p style={{ marginBottom: 0, color: "var(--ink-500)" }}>With best wishes,<br />The {call.funder} Program Team</p>
                        </div>
                        <div style={{ marginTop: 12 }}>
                            <button className="funder-link" onClick={() => navigate(`/funder/proposal?id=${(sel.grantNumber || "").replace(/^HF-\d+-0*/, "")}`)}>
                                Open full proposal <ExternalLink size={13} />
                            </button>
                        </div>
                    </div>
                ) : (
                    <p style={{ color: "var(--ink-400)", fontSize: 13, margin: 0 }}>No proposal selected.</p>
                )}

                <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 16, padding: "12px 14px", background: "var(--success-bg)", borderRadius: 10 }}>
                    <CheckCircle2 size={16} color="var(--success)" style={{ flexShrink: 0, marginTop: 1 }} />
                    <span style={{ fontSize: 12.5, color: "var(--success)", lineHeight: 1.5 }}>{policyNote}</span>
                </div>

                <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                    <button className="funder-btn" onClick={() => setSent(true)}>
                        <Send size={15} /> Send decision letters
                    </button>
                    {sent && <span style={{ fontSize: 12.5, color: "var(--success)", fontWeight: 600 }}>Queued (demo) — {decisions.length} letters</span>}
                </div>
            </SectionCard>
        </div>
    );
}

export default function FunderDecisions() {
    return (
        <FunderShell activePage="Decisions & Feedback">
            {(callId, call) => <Inner callId={callId} call={call} />}
        </FunderShell>
    );
}
